import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Topbar from "@/components/layout/Topbar";
import { StatusBadge } from "@/components/StatusBadge";
import { formatShortDate, relativeTime } from "@/lib/format";
import { Plug, Activity, RefreshCw, AlertTriangle } from "lucide-react";

export default function SuperChannelManager() {
    const [jobs, setJobs] = useState([]);
    const [stats, setStats] = useState({ success: 0, failed: 0 });

    useEffect(() => {
        api.get("/platform/sync-jobs?limit=200").then(({ data }) => {
            setJobs(data);
            setStats({
                success: data.filter((j) => j.status === "success").length,
                failed: data.filter((j) => j.status === "failed").length,
            });
        });
    }, []);

    return (
        <>
            <Topbar title="Channel Manager" subtitle="Native distribution & sync engine" />
            <div className="flex-1 overflow-auto p-6">
                <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
                    <KPI label="Engine status" value="Operational" sub="processing" icon={Activity} tone="green" />
                    <KPI label="Total sync jobs" value={jobs.length} sub="last 48h" icon={Plug} />
                    <KPI label="Successful syncs" value={stats.success} sub={jobs.length ? `${Math.round((stats.success / jobs.length) * 100)}% rate` : "—"} icon={RefreshCw} tone="green" />
                    <KPI label="Failed jobs" value={stats.failed} sub="needs attention" icon={AlertTriangle} tone={stats.failed > 0 ? "red" : "green"} />
                </div>

                <div className="akm-card p-4">
                    <div className="mb-3 akm-section-title">Configuration</div>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        <ConfigRow label="Sync interval" value="15 minutes" />
                        <ConfigRow label="Concurrency" value="8 workers" />
                        <ConfigRow label="Retry policy" value="3 attempts · exponential backoff" />
                        <ConfigRow label="Webhook endpoint" value="/api/webhooks/channels" mono />
                        <ConfigRow label="Default availability strategy" value="Pooled inventory" />
                        <ConfigRow label="Rate parity" value="Enforced across channels" />
                    </div>
                </div>

                <div className="mt-4 akm-card overflow-hidden">
                    <div className="border-b border-border px-4 py-3 akm-section-title">Recent activity</div>
                    <table className="akm-table w-full">
                        <thead><tr><th>Time</th><th>Channel</th><th>Status</th><th>Synced</th><th>Duration</th><th>Error</th></tr></thead>
                        <tbody>
                            {jobs.slice(0, 30).map((j) => (
                                <tr key={j.id}>
                                    <td className="text-muted-foreground">{relativeTime(j.created_at)}</td>
                                    <td className="capitalize font-medium">{j.channel}</td>
                                    <td><StatusBadge status={j.status === "success" ? "active" : "error"} label={j.status} /></td>
                                    <td>{j.synced_count}</td>
                                    <td className="font-mono text-[11px] text-muted-foreground">{j.duration_ms} ms</td>
                                    <td className="text-muted-foreground">{j.error || "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    );
}

const TONE = { green: "text-green-700", amber: "text-amber-700", red: "text-red-700" };
function KPI({ label, value, sub, icon: Icon, tone }) {
    return (
        <div className="akm-card p-3">
            <div className="flex items-center justify-between">
                <div className="akm-section-title">{label}</div>
                {Icon && <Icon size={13} className="text-muted-foreground" />}
            </div>
            <div className={`mt-1 text-lg font-semibold tracking-tight ${TONE[tone] || ""}`}>{value}</div>
            <div className="mt-0.5 text-[11px] text-muted-foreground">{sub}</div>
        </div>
    );
}

function ConfigRow({ label, value, mono }) {
    return (
        <div className="rounded-md border border-border bg-background p-3">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className={`mt-1 text-[13px] ${mono ? "font-mono" : "font-medium"}`}>{value}</div>
        </div>
    );
}
